import { c as _c } from "react/compiler-runtime";
import { gql as apolloGql } from '@apollo/client';
import { useApolloClient, useSubscription } from '@apollo/client/react';
import { Badge, HStack, Icon, Text, VStack } from '@chakra-ui/react';
import { UserAvatar } from '@src/components/sco/Account/UserAvatar';
import { teamAdvisorBadgeForName } from '@src/components/sco/Advisors/model/access';
import { ScoAdvisorEngagement, ScoUser } from '@src/graphql-types';
import { useEffect, useRef, useState } from 'react';
import { LuLock } from 'react-icons/lu';
type EngagementNavListItemTitleProps = {
  engagement?: ScoAdvisorEngagement | null;
  name: string;
  userId?: string | null;
  /** Force a Startups.com team role badge (when not derived from name). */
  badge?: string | null;
  /** Show a lock icon (unpaid users viewing team advisors). */
  locked?: boolean;
  /** Avatar image when no engagement user is available (locked static roster). */
  avatarUrl?: string | null;
  /**
   * Override avatar user (e.g. founder on My Founders nav — engagement.advisorUser is the
   * current advisor, not the row subject).
   */
  avatarUser?: ScoUser | null;
  isAI?: boolean;
};
const ENGAGEMENT_NAV_UNREAD_FRAGMENT = apolloGql`
  fragment EngagementNavUnread on ScoThread {
    id
    threadId
    currentUserUnreadCommentsCount
    lastActiveAt
  }
`;

// Every streaming tick of a reply is an event on this channel; the unread count only moves when a
// comment arrives or is read. So the event carries no thread fields (no Thread read per tick on the
// server), and the count is re-read at most once per UNREAD_REFRESH_MS, with a trailing read.
const ENGAGEMENT_NAV_THREAD_EVENT = apolloGql`
  subscription EngagementNavThreadEvent($threadId: String!) {
    scoThreadComment(threadId: $threadId) {
      id
      commentId
    }
  }
`;
const ENGAGEMENT_NAV_UNREAD_QUERY = apolloGql`
  query EngagementNavUnreadCount($threadId: String) {
    chat {
      id
      thread(threadId: $threadId) {
        ...EngagementNavUnread
      }
    }
  }
  ${ENGAGEMENT_NAV_UNREAD_FRAGMENT}
`;
const UNREAD_REFRESH_MS = 2000;
export const EngagementNavListItemTitle = t0 => {
  const $ = _c(53);
  const {
    engagement,
    name,
    userId,
    badge,
    locked: t1,
    avatarUrl,
    avatarUser: avatarUserProp,
    isAI
  } = t0;
  const locked = t1 === undefined ? false : t1;
  const threadId = engagement?.chatThread?.threadId;
  const initialUnread = engagement?.chatThread?.currentUserUnreadCommentsCount ?? 0;
  const [unreadCount, setUnreadCount] = useState(initialUnread);
  let t2;
  if ($[0] !== initialUnread) {
    t2 = () => {
      setUnreadCount(initialUnread);
    };
    $[0] = initialUnread;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const t3 = engagement?.id;
  let t4;
  if ($[2] !== initialUnread || $[3] !== t3 || $[4] !== threadId) {
    t4 = [t3, initialUnread, threadId];
    $[2] = initialUnread;
    $[3] = t3;
    $[4] = threadId;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  useEffect(t2, t4);
  const apolloClient = useApolloClient();
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      timer: null,
      lastAt: 0
    };
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const unreadRefresh = useRef(t5);
  let t6;
  if ($[7] !== apolloClient || $[8] !== threadId) {
    t6 = () => {
      unreadRefresh.current.lastAt = Date.now();
      apolloClient.query({
        query: ENGAGEMENT_NAV_UNREAD_QUERY,
        variables: {
          threadId
        },
        fetchPolicy: "network-only"
      }).then(result => {
        const count = (result.data as any)?.chat?.thread?.currentUserUnreadCommentsCount;
        if (typeof count === "number") {
          setUnreadCount(count);
        }
      }).catch(_temp);
    };
    $[7] = apolloClient;
    $[8] = threadId;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const readUnread = t6;
  let t7;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => {
      const state = unreadRefresh.current;
      return () => {
        if (state.timer) {
          clearTimeout(state.timer);
        }
      };
    };
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== threadId) {
    t8 = [threadId];
    $[11] = threadId;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  useEffect(t7, t8);
  let t9;
  if ($[13] !== threadId) {
    t9 = {
      threadId
    };
    $[13] = threadId;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  const t10 = !threadId;
  let t11;
  if ($[15] !== readUnread) {
    t11 = () => {
      const state_0 = unreadRefresh.current;
      if (state_0.timer) {
        return;
      }
      const wait = Math.max(0, state_0.lastAt + UNREAD_REFRESH_MS - Date.now());
      state_0.timer = setTimeout(() => {
        state_0.timer = null;
        readUnread();
      }, wait);
    };
    $[15] = readUnread;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  let t12;
  if ($[17] !== t10 || $[18] !== t11 || $[19] !== t9) {
    t12 = {
      variables: t9,
      skip: t10,
      ignoreResults: true,
      onData: t11
    };
    $[17] = t10;
    $[18] = t11;
    $[19] = t9;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  useSubscription(ENGAGEMENT_NAV_THREAD_EVENT, t12);
  let t13;
  if ($[21] !== badge || $[22] !== name) {
    t13 = badge ?? teamAdvisorBadgeForName(name);
    $[21] = badge;
    $[22] = name;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  const teamBadge = t13;
  const showAi = isAI ?? !!engagement?.isAI;
  let t14;
  if ($[24] !== avatarUrl || $[25] !== avatarUserProp || $[26] !== engagement?.advisorUser || $[27] !== name) {
    t14 = avatarUserProp || engagement?.advisorUser || (avatarUrl ? {
      pictureUrl: avatarUrl,
      fullName: name
    } as ScoUser : undefined);
    $[24] = avatarUrl;
    $[25] = avatarUserProp;
    $[26] = engagement?.advisorUser;
    $[27] = name;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  const avatarUser = t14;
  let t15;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = {
      size: "sm"
    };
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  const t16 = userId || undefined;
  let t17;
  if ($[30] !== avatarUser || $[31] !== showAi || $[32] !== t16) {
    t17 = <UserAvatar avatarProps={t15} isAI={showAi} noLink={true} user={avatarUser} userId={t16} />;
    $[30] = avatarUser;
    $[31] = showAi;
    $[32] = t16;
    $[33] = t17;
  } else {
    t17 = $[33];
  }
  let t18;
  if ($[34] !== name) {
    t18 = <Text flex={1} fontWeight="semibold" lineClamp={1} lineHeight="short" minWidth={0} textAlign="start" textStyle="sm">{name}</Text>;
    $[34] = name;
    $[35] = t18;
  } else {
    t18 = $[35];
  }
  let t19;
  if ($[36] !== locked || $[37] !== unreadCount) {
    t19 = unreadCount > 0 && !locked && <Badge flexShrink={0} variant="count">{unreadCount}</Badge>;
    $[36] = locked;
    $[37] = unreadCount;
    $[38] = t19;
  } else {
    t19 = $[38];
  }
  let t20;
  if ($[39] !== locked) {
    t20 = locked ? <Icon as={LuLock} boxSize={3} color="fg.muted" flexShrink={0} /> : null;
    $[39] = locked;
    $[40] = t20;
  } else {
    t20 = $[40];
  }
  let t21;
  if ($[41] !== t18 || $[42] !== t19 || $[43] !== t20) {
    t21 = <HStack align="center" gap={1.5} minWidth={0} width="full">{t18}{t19}{t20}</HStack>;
    $[41] = t18;
    $[42] = t19;
    $[43] = t20;
    $[44] = t21;
  } else {
    t21 = $[44];
  }
  let t22;
  if ($[45] !== teamBadge) {
    t22 = teamBadge ? <Text color="fg.muted" lineClamp={1} minWidth={0} textAlign="start" textStyle="xs">{teamBadge}</Text> : null;
    $[45] = teamBadge;
    $[46] = t22;
  } else {
    t22 = $[46];
  }
  let t23;
  if ($[47] !== t21 || $[48] !== t22) {
    t23 = <VStack align="stretch" flex={1} gap={0} minWidth={0} paddingTop="1px">{t21}{t22}</VStack>;
    $[47] = t21;
    $[48] = t22;
    $[49] = t23;
  } else {
    t23 = $[49];
  }
  let t24;
  if ($[50] !== t17 || $[51] !== t23) {
    t24 = <HStack align="flex-start" gap={2.5} minWidth={0} width="full">{t17}{t23}</HStack>;
    $[50] = t17;
    $[51] = t23;
    $[52] = t24;
  } else {
    t24 = $[52];
  }
  return t24;
};
function _temp(err) {
  return console.error(err);
}